import type { ClassConstructor } from 'class-transformer';
import { plainToInstance, Transform } from 'class-transformer';

/**
 * Parse a JSON-encoded query string value into an object/array, optionally
 * instantiating it as a class via `options.type`. Also accepts already-parsed
 * objects/arrays (e.g. when the value arrives in the POST body), and applies
 * `plainToInstance` to those too when `type` is set.
 *
 * `options.defaultValue` is applied when the value is null or undefined. Note
 * that class-transformer never invokes a @Transform for properties that are
 * MISSING from the input — to default a missing property, set the default on
 * the property declaration itself: `@ParseJson() data = { foo: 1 };`.
 */
export function ParseJson<TModel = unknown>(
	options?: ParseJsonOptions<TModel>,
): PropertyDecorator {
	return Transform(
		({ value }) => {
			const type: ClassConstructor<unknown> | undefined = options?.type;

			if (value == null && options?.defaultValue != null) {
				return options.defaultValue;
			}

			let resolved: unknown = value;

			if (typeof value === 'string') {
				try {
					resolved = JSON.parse(value) as unknown;
				} catch {
					return value;
				}
			}

			if (type != null && Array.isArray(resolved)) {
				return resolved.map((item) => plainToInstance(type, item));
			}

			if (type != null && typeof resolved === 'object' && resolved != null) {
				return plainToInstance(type, resolved);
			}

			return resolved;
		},
		{ toClassOnly: true },
	);
}

interface ParseJsonOptions<TModel> {
	type?: ClassConstructor<unknown>;
	defaultValue?: TModel | Array<TModel>;
}
